import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  ImageList, 
  ImageListItem, 
  Modal,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';

const styles = {
  gallery: {
    mt: 4,
    mb: 2,
  },
  title: {
    mb: 2,
    color: 'text.secondary',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  image: {
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  imageListItem: {
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'scale(1.05)',
      zIndex: 1,
    },
  },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1300,
  },
  modalImage: {
    maxHeight: '85vh',
    maxWidth: '90vw',
    objectFit: 'contain',
    transition: 'transform 0.2s',
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    color: 'white',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
    }
  }
};

const ImageGallery = ({ 
  images, 
  allowDelete = false,
  onDeleteImage,
  rowHeight = 200,
  cols = 3
}) => {
  const [selectedImage, setSelectedImage] = useState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Responsive columns
  const responsiveCols = isMobile ? 2 : (isTablet ? 3 : cols);
  const responsiveRowHeight = isMobile ? 150 : rowHeight;

  if (images.length === 0) {
    return null;
  }

  return (
    <>
      <Box sx={styles.gallery}>
        <Typography variant="h6" sx={styles.title}>
          <ZoomOutMapIcon fontSize="small" />
          Images ({images.length})
        </Typography>
        <ImageList cols={responsiveCols} rowHeight={responsiveRowHeight} gap={isMobile ? 4 : 8}>
          {images.map((img, index) => (
            <ImageListItem 
              key={index}
              sx={{ ...styles.image, ...styles.imageListItem }}
              >
              <img
                src={img.url || img.preview}
                alt={`Upload ${index + 1}`}
                loading="lazy"
                style={{ height: responsiveRowHeight, objectFit: 'cover' }}
                onClick={() => setSelectedImage(img)}
              />
              {allowDelete && (
                <IconButton
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    position: 'absolute',
                    right: 4,
                    top: 4,
                    bgcolor: 'background.paper'
                  }}
                  onClick={() => onDeleteImage(index, img.isExisting)}
                >
                  <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              )}
            </ImageListItem>
          ))}
        </ImageList>
      </Box>
      
      { selectedImage && (
        <Modal
          open={!!selectedImage}
          aria-labelledby="image-modal"
          onClick={() => setSelectedImage(null)}
          onClose={() => setSelectedImage(null)}
          >
          <Box sx={styles.modalBackdrop}>
            <IconButton 
              onClick={() => setSelectedImage(null)}
              sx={styles.closeButton}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={selectedImage.url || selectedImage.preview}
              alt="Full size"
              style={styles.modalImage}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        </Modal>
      )}
    </>
  );
};

export default ImageGallery; 